/**
 * RelatedBookingsTable - Display related bookings for a customer
 */

import React, { useCallback } from "react";
import { Table, Tag, Button, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

const { Text, Link } = Typography;

const RelatedBookingsTable = ({ bookings = [] }) => {
  const navigate = useNavigate();

  const viewBooking = useCallback(
    (record) => {
      if (record.orderId) {
        navigate(`/orders/${record.orderId}`);
      }
    },
    [navigate]
  );

  const columns = [
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
      render: (orderId, record) => (
        <Link
          onClick={(event) => {
            event.stopPropagation();
            viewBooking(record);
          }}
        >
          <Text code>{orderId || "N/A"}</Text>
        </Link>
      ),
    },
    {
      title: "Move Date",
      dataIndex: "moveDate",
      key: "moveDate",
      render: (date) => {
        if (!date) return "N/A";
        try {
          return format(new Date(date), "MMM dd, yyyy");
        } catch {
          return "Invalid date";
        }
      },
    },
    {
      title: "Time Window",
      dataIndex: ["pickupTimeWindow", "type"],
      key: "timeWindow",
      render: (type, record) => {
        const window = record.pickupTimeWindow;
        if (!window) return "N/A";
        if (window.start && window.end) {
          return `${window.start} - ${window.end}`;
        }
        return type || "N/A";
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusMap = {
          pending: { color: "gold", text: "Pending" },
          scheduled: { color: "blue", text: "Scheduled" },
          confirmed: { color: "green", text: "Confirmed" },
          in_progress: { color: "cyan", text: "In Progress" },
          completed: { color: "green", text: "Completed" },
          cancelled: { color: "red", text: "Cancelled" },
        };
        const cfg = statusMap[status] || { color: "default", text: status || "N/A" };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: "Team",
      dataIndex: ["assignedTeam", "teamName"],
      key: "team",
      render: (teamName) => teamName || "Unassigned",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => {
        if (!date) return "N/A";
        try {
          return format(new Date(date), "MMM dd, yyyy");
        } catch {
          return "Invalid date";
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          aria-label="View booking"
          onClick={(event) => {
            event.stopPropagation();
            viewBooking(record);
          }}
          style={{ opacity: 0.6 }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
        />
      ),
    },
  ];

  if (bookings.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <Text type="secondary">No bookings found for this customer</Text>
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={bookings}
      rowKey={(record) => record.orderId || record._id}
      pagination={{ pageSize: 10 }}
      size="small"
      scroll={{ x: "max-content" }}
      onRow={(record) => ({
        onClick: () => viewBooking(record),
        style: { cursor: "pointer" },
      })}
    />
  );
};

export default RelatedBookingsTable;
